'use client';

import React, { use, useState } from 'react';
import LaporanHeader from '@/components/yayasan/keuangan/LaporanHeader';
import { SchoolUnit, validUnits, getUnitJournal, getSchoolData } from '@/lib/data';
import { notFound } from 'next/navigation';
import { Library, Search, ChevronDown, ListFilter, Banknote, Wallet, Receipt } from 'lucide-react';

export default function BukuBesarPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);
    const [activeUnit, setActiveUnit] = useState<SchoolUnit>(initialUnit as SchoolUnit);

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const unitJournal = getUnitJournal(activeUnit);
    const schoolData = getSchoolData(activeUnit);
    const baseType = activeUnit.split('-')[0];
    const unitAccounts = ((schoolData as any)[`${baseType}Accounts`] || []) as any[];
    const ACCOUNTS = unitAccounts.map(acc => acc.name);

    const [selectedAccount, setSelectedAccount] = useState(ACCOUNTS[0] || 'Kas Utama');

    const ledgerEntries = unitJournal
        .filter(j => j.debitAccount.includes(selectedAccount) || j.creditAccount.includes(selectedAccount))
        .map(j => ({
            date: j.date,
            ref: j.reference,
            desc: j.description,
            debit: j.debitAccount.includes(selectedAccount) ? j.amount : '-',
            credit: j.creditAccount.includes(selectedAccount) ? j.amount : '-',
            balance: j.amount // Simplified for mock
        }));

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700">
            <LaporanHeader
                title="Buku Besar"
                description="Detail mutasi dan histori saldo untuk setiap akun keuangan"
                activeUnit={activeUnit}
                onUnitChange={setActiveUnit}
            />

            {/* Account Selector */}
            <div className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-8 group">
                <div className="flex items-center gap-5">
                    <div className="w-14 h-14 bg-primary text-white rounded-2xl flex items-center justify-center shadow-xl shadow-primary/20 group-hover:scale-105 transition-transform">
                        <Library className="w-7 h-7" />
                    </div>
                    <div className="space-y-1">
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Akun yang terpilih</p>
                        <div className="flex items-center gap-3">
                            <h2 className="text-2xl font-bold tracking-tight">{selectedAccount}</h2>
                            <span className="px-3 py-1 bg-primary-soft text-primary text-[10px] font-bold uppercase tracking-wider rounded-lg border border-primary/10">Aktif</span>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="relative w-full md:w-72">
                        <select
                            className="w-full pl-6 pr-12 py-4 bg-background border-2 border-border-main hover:border-primary/30 rounded-2xl text-sm font-bold appearance-none outline-none focus:ring-4 focus:ring-primary/10 transition-all cursor-pointer shadow-sm"
                            value={selectedAccount}
                            onChange={(e) => setSelectedAccount(e.target.value)}
                        >
                            {ACCOUNTS.map(acc => <option key={acc} value={acc}>{acc}</option>)}
                        </select>
                        <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted pointer-events-none" />
                    </div>

                    <button className="w-full md:w-auto px-8 py-4 bg-surface border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-primary-soft/50 group transition-all flex items-center justify-center gap-3">
                        <ListFilter className="w-4 h-4 text-primary" />
                        Semua Akun
                    </button>
                </div>
            </div>

            {/* Ledger Table */}
            <div className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 duration-1000">
                <div className="p-7 border-b border-border-main bg-background/30 flex items-center justify-between">
                    <h3 className="font-bold text-xl flex items-center gap-3">
                        <Receipt className="w-5 h-5 text-primary" /> Histori Transaksi Akun
                    </h3>
                    <div className="flex items-center gap-4">
                        <div className="flex flex-col items-end">
                            <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Saldo Berjalan</span>
                            <span className="text-xl font-bold text-primary tabular-nums">Rp 137.750.000</span>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-background/50 border-b border-border-main">
                            <tr>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Waktu</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Ref</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Keterangan</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Debit</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Kredit</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Saldo</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main">
                            {ledgerEntries.map((l: any, i: number) => (
                                <tr key={i} className="hover:bg-primary-soft/5 transition-colors group">
                                    <td className="px-6 py-6 font-bold text-foreground text-sm">{l.date}</td>
                                    <td className="px-6 py-6">
                                        <span className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 bg-background border border-border-main rounded-lg text-secondary group-hover:text-primary transition-colors">
                                            {l.ref}
                                        </span>
                                    </td>
                                    <td className="px-6 py-6 font-medium text-secondary group-hover:text-foreground transition-colors">{l.desc}</td>
                                    <td className="px-6 py-6 text-right font-bold text-success tabular-nums">{l.debit !== '-' ? `+ ${l.debit}` : '-'}</td>
                                    <td className="px-6 py-6 text-right font-bold text-error tabular-nums">{l.credit !== '-' ? `- ${l.credit}` : '-'}</td>
                                    <td className="px-6 py-6 text-right font-bold text-foreground bg-primary-soft/10 group-hover:bg-primary-soft/20 transition-all tabular-nums text-lg">
                                        {l.balance}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="p-8 bg-primary/5 flex items-center justify-between border-t border-border-main">
                    <div className="flex gap-10">
                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Debit</p>
                            <p className="text-xl font-bold text-success">Rp 60.000.000</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Kredit</p>
                            <p className="text-xl font-bold text-error">Rp 7.250.000</p>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
